import {typeConverter, useFirestoreResults, useFirestoreSnapshot} from '@goschool/react-firebase'
import type {User} from 'firebase/auth'
import type {DocumentSnapshot, QueryDocumentSnapshot, Timestamp} from 'firebase/firestore'
import {collection, doc, orderBy, query} from 'firebase/firestore'
import {useMemo} from 'react'
import type {Course, CourseStudent, CourseStudentStatus} from '../Course'

export function resolveCourseStudentStatus(student: CourseStudent): CourseStudentStatus {
  const status = student.status
  if (status === 'active' || status === 'suspended' || status === 'excluded') {
    return status
  }

  if (student.excluded) {
    return 'excluded'
  }

  const suspendedUntil = student.suspended_until as Timestamp | undefined | null
  if (suspendedUntil?.toMillis != null && suspendedUntil.toMillis() > Date.now()) {
    return 'suspended'
  }

  return 'active'
}

export function useCourseStudents(courseSnapshot: QueryDocumentSnapshot<Course> | null | undefined) {
  const q = useMemo(() => {
    if (courseSnapshot == null) {
      return undefined
    }
    return query(
      collection(courseSnapshot.ref, 'students').withConverter(
        typeConverter<CourseStudent>()
      ),
      orderBy('name', 'asc')
    )
  }, [courseSnapshot])

  return useFirestoreResults(q)
}

export function useCourseStudent(
  courseSnapshot: QueryDocumentSnapshot<Course> | null | undefined,
  user: User | null | undefined
): DocumentSnapshot<CourseStudent> | null | undefined {
  const ref = useMemo(() => {
    if (courseSnapshot == null || user === undefined) {
      return undefined
    }
    if (user == null) {
      return undefined
    }
    return doc(courseSnapshot.ref, 'students', user.uid).withConverter(
      typeConverter<CourseStudent>()
    )
  }, [courseSnapshot, user])

  const snapshot = useFirestoreSnapshot(ref)
  return snapshot
}
